import { FileDescriptor } from "@archipad-js/core/utils";

export interface PatchUserInfo {
    company_id: number;
    email: string;
    id: number;
}

export type PatchId = string;

/**
 *  A patch is a list of operations on orm entities
 */
export interface Patch {
    /** patch header */
    readonly header: PatchHeader;
    /** entity operations by entity type name */
    readonly operationsByType: ReadonlyMap<string, EntityOperations>;
}

export interface PatchHeader {
    /** minimum model version required to read this patch */
    readonly modelMinVersion: number;
    /** model version of this patch */
    readonly modelVersion: number;
    /** client name */
    readonly clientName?: string;
    /* user info */
    readonly user?: Readonly<PatchUserInfo>;
    /* temp patch */
    readonly temp?: boolean;
    /* if true, represent an empty patch from a regular patch point of view but is not as a temp patch */
    readonly emptyLike?: boolean;
    /* if the patch is a dump of the project or not */
    readonly isDump?: boolean;
}

/**
 * A patch in the network format (a JSON serializable structure)
 */
export interface NetPatch {
    patch: NetPatchData;
    attachmentManifest: NetPatchAttachmentManifest;
}

export type NetPatchData = [NetPatchHeader, ...Array<NetPatchOperations>];

export interface NetPatchAttachmentManifest {
    [entityId: string]: {
        [attachmentKey: string]: {
            url: string;
            deleted: boolean;
        };
    };
}

/**
 * A patch header in the network format
 */
export interface NetPatchHeader {
    diff: boolean;
    minVersion: number;
    version: number;
    client?: string;
    user?: PatchUserInfo;
    temp?: boolean;
    tempData?: {
        emptyLike: boolean;
    };
    templateIdStack?: string;
}

export interface NetPatchOperation {
    id: string;
    _metadata?: { op: "update" | "delete" };
    [k: string]: unknown;
}

export type NetPatchOperations = [string, ...Array<NetPatchOperation>];

export interface EntityOperations {
    /** entity operations by entity id */
    readonly operationById: ReadonlyMap<string, EntityOperation>;
}

export interface AttachmentUpdate {
    readonly file: FileDescriptor;
    readonly deleted: false;
}

export interface AttachmentDelete {
    readonly deleted: true;
}

export type AttachmentOperation = AttachmentUpdate | AttachmentDelete;

export interface EntityUpdate {
    readonly type: "update";
    readonly propertyUpdates: ReadonlyMap<string, any>;
    readonly attachmentUpdates: ReadonlyMap<string, AttachmentOperation>;
}

export interface EntityCreate {
    readonly type: "create";
    readonly propertyUpdates: ReadonlyMap<string, any>;
    readonly attachmentUpdates: ReadonlyMap<string, AttachmentOperation>;
}

export interface EntityDelete {
    readonly type: "delete";
}

export type EntityOperation = EntityUpdate | EntityCreate | EntityDelete;

export enum PatchNature {
    PatchData = "patchData",
    DumpPatch = "dumpPatch",
    LinkedEmptyPatch = "linkedEmptyPatch",
    TempPatch = "tempPatch",
}

export interface PatchData extends NetPatch {
    patchId: string;
    patch: any;
    attachmentManifest: NetPatchAttachmentManifest;
}
